import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ClipboardList } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { MyFormList } from '@up2d8/contracts';
import { Badge, Card, CardTitle } from '@up2d8/ui';
import { absentOrThrow } from '../../shared/home-card';
import { useSession } from '../../shared/session';

/**
 * De ingang naar de formulieren, op het beginscherm (Stap 37).
 *
 * **Geen zesde tab.** De onderbalk heeft er vijf, en die zijn alle vijf bestemmingen waar je
 * meermaals per week komt. Een verlofaanvraag doe je een paar keer per jaar; een tab daarvoor zou
 * de vijf andere smaller maken. Zelfde afweging als bij het zoekveld in Stap 34d.
 *
 * De kaart verdwijnt als er niets in te vullen valt — dat is tegelijk de weg waarop deze module
 * uit kan staan: geen formulieren, geen recht of module uit betekent allemaal "geen kaart", en
 * geen leeg vak met een uitleg erbij.
 */
export function AppFormsCard() {
  const { t } = useTranslation();
  const { api } = useSession();

  const forms = useQuery({
    queryKey: ['me', 'forms'],
    queryFn: async () => {
      const { data, error, response } = await api.GET('/api/v1/me/forms', {});
      // Geen recht of module uit: geen kaart. Een storing is iets anders — zie de helper.
      if (error || !data) return absentOrThrow(response, error);
      return (data as MyFormList).items;
    },
  });

  const items = forms.data ?? [];
  if (items.length === 0) return null;

  const open = items.reduce((total, form) => total + form.openOfMine, 0);

  return (
    <Card compact data-app-card>
      <Link to="/app/formulieren" className="flex min-h-12 items-center gap-element">
        <span aria-hidden className="rounded-pill bg-brand-primary-tint p-2 text-brand-primary">
          <ClipboardList className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <CardTitle>{t('appForms.title')}</CardTitle>
          <span className="block text-small text-text-secondary">
            {t('appForms.cardHint', { count: items.length })}
          </span>
        </span>
        {open > 0 ? (
          <Badge tone="warning">{t('appForms.openOfMine', { count: open })}</Badge>
        ) : null}
      </Link>
    </Card>
  );
}
